import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import CreateUser from './components/buyers/CreateUser';
import CreateLead from './components/buyers/CreateLead';
import SellersCreateUser from './components/sellers/CreateUser';
import SellersCreateLead from './components/sellers/CreateLead';
import BuyersHome from './components/buyers/Home';
import SellersHome from './components/sellers/Home';
import LandingPage from './components/general/LandingPage';
import NewLandingPage from './components/general/NewLandingPage';
import GetStarted from './components/general/GetStarted';
import AdminDashboard from './components/admin/Dashboard';
import { AuthProvider, AuthContext } from './auth/AuthContext';
import Login from './auth/Login';
import Register from './components/general/Register';
import Profile from './auth/Profile';
import Dashboard from './auth/Dashboard';
import './App.css';
import Navigation from './components/navigation/Navigation';
import Footer from './components/general/Footer';
import AdminLogin from './components/admin/Login';
import UserManagement from './components/admin/UserManagement';
import AuthCallback from './auth/AuthCallback';
import RelayMessaging from './components/messaging/RelayMessaging';

// ScrollToTop component to reset scroll position when navigating
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

const PrivateRouteAdmin = ({ children }) => {
    const { auth, userProfile } = useContext(AuthContext);
    
    // Check if user is authenticated and has admin privileges
    const isAdmin = auth && userProfile && (userProfile.user_type === 'admin' || userProfile.user_type === 'support');
    
    // If not authenticated, redirect to admin login
    if (!auth) {
        return <Navigate to="/admin/login" />;
    }
    
    // If authenticated but not admin, redirect to regular dashboard
    if (!isAdmin) {
        return <Navigate to="/dashboard" />;
    }
    
    // If admin, allow access
    return children;
};

const PrivateRoute = ({ children }) => {
    const { auth } = useContext(AuthContext);
    return auth ? children : <Navigate to="/login" />;
};

const Layout = ({ children }) => {
    const location = useLocation();
    const isAdminPath = location.pathname.includes('/admin');

    return (
        <div className="App">
            {!isAdminPath && <Navigation />}
            {children}
            {!isAdminPath && <Footer />}
        </div>
    );
};

function App() {
    return (
        <Router>
            <AuthProvider>
                <Layout>
                    <ScrollToTop />
                    <Routes>
                        <Route path="/" element={<NewLandingPage />} />
                        <Route path="/new-lander" element={<NewLandingPage />} />
                        <Route path="/get-started" element={<GetStarted />} />
                        <Route path="/buyers" element={<BuyersHome />} />
                        <Route path="/sellers" element={<SellersHome />} />
                        <Route path="/admin" element={<PrivateRouteAdmin><AdminDashboard /></PrivateRouteAdmin>} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/buyers/create_user" element={<CreateUser />} />
                        <Route path="/buyers/create_lead" element={<CreateLead />} />
                        <Route path="/sellers/create_user" element={<SellersCreateUser />} />
                        <Route path="/sellers/create_lead" element={<SellersCreateLead />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin/users" element={<PrivateRouteAdmin><UserManagement /></PrivateRouteAdmin>} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/messaging/:relayId" element={<PrivateRoute><RelayMessaging /></PrivateRoute>} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                    </Routes>
                </Layout>
            </AuthProvider>
        </Router>
    );
}

// Simple Terms of Service page
const TermsOfService = () => {
    return (
        <div className="legal-page">
            <h1>Terms of Service</h1>
            <div className="legal-content">
                <h2>1. Acceptance of Terms</h2>
                <p>By accessing or using Connexierge, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site.</p>
                
                <h2>2. Use License</h2>
                <p>Permission is granted to temporarily use Connexierge for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title.</p>
                
                <h2>3. Disclaimer</h2>
                <p>The materials on Connexierge are provided on an 'as is' basis. Connexierge makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.</p>
                
                <h2>4. Limitations</h2>
                <p>In no event shall Connexierge or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use Connexierge, even if Connexierge or a Connexierge authorized representative has been notified orally or in writing of the possibility of such damage.</p>
            </div>
        </div>
    );
};

// Simple Privacy Policy page
const PrivacyPolicy = () => {
    return (
        <div className="legal-page">
            <h1>Privacy Policy</h1>
            <div className="legal-content">
                <h2>1. Information We Collect</h2>
                <p>We collect information you provide directly to us, such as when you create or modify your account, request services, contact customer support, or otherwise communicate with us.</p>
                
                <h2>2. How We Use Information</h2>
                <p>We may use the information we collect to provide, maintain, and improve our services, to process and complete transactions, and to send you related information including confirmations, invoices, technical notices, updates, security alerts, and support and administrative messages.</p>
                
                <h2>3. Information Sharing</h2>
                <p>We may share the information we collect with third parties who need to access it in order to do work on our behalf, including to our service providers who provide services such as website hosting, data analysis, payment processing, and customer service.</p>
                
                <h2>4. Security</h2>
                <p>We take reasonable measures to help protect information about you from loss, theft, misuse and unauthorized access, disclosure, alteration and destruction.</p>
            </div>
        </div>
    );
};

export default App;
